import React from 'react';
import { useState, useEffect } from 'react';
import {
    Input,
    CalendarPicker,
    Button,
    Checkbox,
    CheckboxGroup,
} from '@appkit4/react-components';
import './ProcessingFields.scss';
import { GetExtractedFieldsDTO } from '../../../api/generated/models';

interface ProcessingFieldsProps {
    fields: GetExtractedFieldsDTO[];
    onUpdate: (field: string, value: any, checkboxSelectedValues: any) => void;
    onProceed: () => void;
    onNext: () => void;
    isSubmitting: boolean;
    isProceeding: boolean;
    hasNextDocument: boolean;
}

const ProcessingFields: React.FC<ProcessingFieldsProps> = ({
    onUpdate,
    onProceed,
    onNext,
    fields,
    isSubmitting,
    isProceeding,
    hasNextDocument,
}) => {
    const onDateChange =
        (field: string) => (date: Date | null, isValid: boolean) => {
            if (isValid && date) {
                const formattedDate = formatDateForAPI(date);
                onUpdate(field, formattedDate, null);
            }
        };

    const formatDateForAPI = (date: Date): string => {
        return date.toISOString();
    };

    const parseDateFromAPI = (dateString: string): Date | null => {
        try {
            const parsedDate = new Date(dateString);
            return isNaN(parsedDate.getTime()) ? null : parsedDate;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const renderField = (fieldData: GetExtractedFieldsDTO) => {
        const { fieldName, value, checkboxAllValues, checkboxSelectedValues } =
            fieldData;

        switch (true) {
            case (fieldName ?? '').toLowerCase().includes('date'): {
                const dateValue = value
                    ? parseDateFromAPI(value as string)
                    : null;
                return (
                    <CalendarPicker
                        value={dateValue}
                        format="YYYY-MM-DD"
                        fieldTitle={fieldName ?? ''}
                        onChange={onDateChange(fieldName ?? '')}
                    />
                );
            }

            case fieldData.checkboxAllValues !== null: {
                const [selectedValues, setSelectedValues] = useState<
                    (string | number)[]
                >(checkboxSelectedValues || []);
                const singleValue =
                    fieldData.checkboxAllValues &&
                    fieldData.checkboxAllValues.length === 1;

                const handleCheckboxChange = (
                    newSelectedValues: (string | number)[]
                ) => {
                    setSelectedValues(newSelectedValues);
                    onUpdate(fieldName ?? '', value, newSelectedValues);
                };

                useEffect(() => {
                    if (checkboxSelectedValues) {
                        setSelectedValues(checkboxSelectedValues);
                    }
                }, [checkboxSelectedValues]);

                return singleValue ? (
                    <Checkbox
                        checked={selectedValues.includes(
                            (checkboxAllValues ?? [])[0]
                        )}
                        onChange={(checked: boolean) => {
                            if (checked) {
                                const val = (checkboxAllValues ?? [])[0] ?? '';
                                handleCheckboxChange([val]);
                            } else {
                                handleCheckboxChange([]);
                            }
                        }}
                    ></Checkbox>
                ) : (
                    <CheckboxGroup
                        value={selectedValues}
                        onChange={handleCheckboxChange}
                    >
                        {(fieldData.checkboxAllValues ?? []).map((option) => (
                            <Checkbox key={option} value={option}>
                                {option}
                            </Checkbox>
                        ))}
                    </CheckboxGroup>
                );
            }

            default:
                return (
                    <Input
                        value={(value as string) || ''}
                        onChange={(value: string) =>
                            onUpdate(fieldName ?? '', value, null)
                        }
                    />
                );
        }
    };

    return (
        <div className="processing-fields-container">
            <div className="processing-fields">
                {fields.map((field) => (
                    <div className="field-row" key={field.fieldId}>
                        <div className="field-content">
                            <div className="field-cell field-name">
                                {field.fieldName}
                            </div>
                            <div className="field-cell extracted-value-cell">
                                {renderField(field)}
                                {field.confidence !== null &&
                                    field.validationErrorType !== 1 && (
                                        <div className="confidence-indicator">
                                            {field.validationErrorType === 0 ? (
                                                <span className="Appkit4-icon icon-warning-fill status-icon warning-icon  custom-warning-icon" />
                                            ) : field.validationErrorType ===
                                              2 ? (
                                                <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                                            ) : (field.confidence ?? 0) >= 1 ? (
                                                <span className="Appkit4-icon icon-circle-checkmark-fill status-icon success-icon" />
                                            ) : null}
                                            <span className="confidence-text">
                                                {field.validationErrorType === 0
                                                    ? `${
                                                          ((Number(
                                                              field.confidence
                                                          )?.toFixed(2) ??
                                                              0) as unknown as number) *
                                                          100
                                                      }% chance of being correct`
                                                    : field.validationErrorType ===
                                                      2
                                                    ? 'System error'
                                                    : (field.confidence ?? 0) >=
                                                      1
                                                    ? 'User Approved'
                                                    : ''}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </div>
                        {field.confidence !== null &&
                            field.validationErrorType === 1 && (
                                <div className="validation-error">
                                    <span className="Appkit4-icon icon-warning-fill status-icon error-icon" />
                                    <span className="confidence-text">
                                        {field.log || 'Validation failed'}
                                    </span>
                                </div>
                            )}
                    </div>
                ))}
            </div>
            <div className="proceed-button-container">
                <Button
                    variant="primary"
                    onClick={onProceed}
                    className="proceed-button"
                    loading={isSubmitting}
                    disabled={isSubmitting || isProceeding}
                    icon="icon-save-outline"
                >
                    Submit
                </Button>
                <Button
                    variant="primary"
                    className="proceed-button"
                    onClick={onNext}
                    loading={isProceeding}
                    disabled={isSubmitting || isProceeding || !hasNextDocument}
                    icon="icon-arrow-right-outline"
                >
                    Submit & Proceed to Next
                </Button>
            </div>
        </div>
    );
};

export default ProcessingFields;
